import React, { useState, useEffect, useRef } from 'react';
import { FaPen } from 'react-icons/fa';
import { useServices } from '../../ViewModel/ServiceViewModel.js';
import { panelItemsToText, textToPanelItems } from '../../Model/Services/servicePanelUtils.js';
import AddService from './AddService.js';
import '../styles/Services.css';
import '../styles/Admin.css';

const FALLBACK_SERVICE_IMAGE = '/Images/works.jpg';

function Services({ adminMode = false }) {
  const {
    services,
    loading,
    error,
    updateService,
    deleteService,
    refreshServices,
    uploadServiceImage,
  } = useServices();
  const [editingService, setEditingService] = useState(null);
  const [editForm, setEditForm] = useState({
    title: '',
    content: '',
    buttonCTA: '',
    image: '',
    includedIntro: '',
    includedItemsText: '',
    fitsIntro: '',
    fitsItemsText: '',
  });
  const [editImageFile, setEditImageFile] = useState(null);
  const [adminMessage, setAdminMessage] = useState('');
  const [saving, setSaving] = useState(false);
  const [showAdminStrip, setShowAdminStrip] = useState(false);
  const editCardRef = useRef(null);
  const adminStripRef = useRef(null);
  const serviceCardRefs = useRef({});

  const navigateToServiceDetail = (serviceId) => {
    if (adminMode) return;
    const targetPath = `/tjanster/${serviceId}`;
    if (window.location.pathname === targetPath) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    window.history.pushState({}, '', targetPath);
    window.dispatchEvent(new PopStateEvent('popstate'));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const scrollToElement = (element, block = 'center') => {
    if (!element) return;
    element.scrollIntoView({ behavior: 'smooth', block });
  };

  const setServiceCardRef = (serviceId, element) => {
    if (element) {
      serviceCardRefs.current[serviceId] = element;
    }
  };

  useEffect(() => {
    if (!editingService || !adminMode) return;
    setTimeout(() => {
      scrollToElement(editCardRef.current, 'start');
    }, 80);
  }, [editingService, adminMode]);

  if (loading) return <p>Laddar tjänster...</p>;
  if (error) return <p>Fel: {error}</p>;

  const startEdit = (service) => {
    setEditingService(service);
    setEditForm({
      title: service.title || '',
      content: service.content || '',
      buttonCTA: service.buttonCTA || '',
      image: service.image || '',
      includedIntro: service.includedIntro || '',
      includedItemsText: panelItemsToText(service.includedItems),
      fitsIntro: service.fitsIntro || '',
      fitsItemsText: panelItemsToText(service.fitsItems),
    });
    setAdminMessage('');
  };

  const handleToggleAdminStrip = () => {
    setShowAdminStrip((previous) => {
      const next = !previous;
      if (next) {
        setTimeout(() => {
          scrollToElement(adminStripRef.current, 'start');
        }, 80);
      }
      return next;
    });
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleEditFileChange = (e) => {
    const file = e.target.files?.[0];
    setEditImageFile(file || null);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!editingService) return;
    setSaving(true);
    setAdminMessage('');
    const updatedServiceId = editingService.id;
    try {
      let imageUrl = editForm.image || '';
      if (editImageFile) {
        const uploadResult = await uploadServiceImage(editImageFile);
        imageUrl = uploadResult.publicUrl;
      }

      const { includedItemsText, fitsItemsText, ...rest } = editForm;
      await updateService(editingService.id, {
        ...rest,
        image: imageUrl,
        includedItems: textToPanelItems(includedItemsText),
        fitsItems: textToPanelItems(fitsItemsText),
      });
      setEditingService(null);
      setEditForm({
        title: '',
        content: '',
        buttonCTA: '',
        image: '',
        includedIntro: '',
        includedItemsText: '',
        fitsIntro: '',
        fitsItemsText: '',
      });
      setEditImageFile(null);
      setAdminMessage('Tjänst uppdaterad.');

      setTimeout(() => {
        scrollToElement(serviceCardRefs.current[updatedServiceId], 'center');
      }, 100);
    } catch (err) {
      setAdminMessage(`Fel: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    setAdminMessage('');
    try {
      await deleteService(id);
    } catch (err) {
      setAdminMessage(`Fel: ${err.message}`);
    }
  };

  const handleCreated = async () => {
    setAdminMessage('Tjänst tillagd.');
    await refreshServices();
  };

  return (
    <div className="services-container" id="tjanster">
      <div className="services-header">
        <h1>Våra tjänster</h1>
        {adminMode && (
          <button
            className="admin-toggle"
            aria-label="Öppna adminpanel"
            onClick={handleToggleAdminStrip}
          >
            <FaPen />
          </button>
        )}
      </div>

      {adminMode && showAdminStrip && (
        <div className="admin-strip" ref={adminStripRef}>
          <div className="admin-strip-info">
            <span className="admin-dot" />
            <span>Adminläge: redigera tjänster</span>
          </div>
          <div className="admin-strip-actions">
            <AddService onCreated={handleCreated} />
          </div>
        </div>
      )}

      <div className={`services-grid ${adminMode ? 'services-grid--admin' : ''}`}>
        {services.map((service) => (
          <div
            key={service.id}
            className="service-card"
            ref={(element) => setServiceCardRef(service.id, element)}
            role="button"
            tabIndex={0}
            onClick={() => navigateToServiceDetail(service.id)}
            onKeyDown={(event) => {
              if (adminMode) return;
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                navigateToServiceDetail(service.id);
              }
            }}
          >
            <div className="service-img">
              <img src={service.image || FALLBACK_SERVICE_IMAGE} alt={service.title} />
            </div>
            <div className="service-title">
              <h3>{service.title}</h3>
            </div>
            <div className="service-content">
              <p>{service.content}</p>
              <button
                className="service-plus"
                onClick={(event) => {
                  event.stopPropagation();
                  navigateToServiceDetail(service.id);
                }}
                aria-label={service.buttonCTA || 'Läs mer'}
              >
                +
              </button>
              {adminMode && (
                <div className="admin-card-actions" onClick={(event) => event.stopPropagation()}>
                  <button className="admin-ghost" onClick={(event) => { event.stopPropagation(); startEdit(service); }}>
                    <FaPen style={{ marginRight: 6 }} /> Redigera
                  </button>
                  <button className="admin-ghost danger" onClick={(event) => { event.stopPropagation(); handleDelete(service.id); }}>Ta bort</button>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {adminMode && editingService && (
        <div className="admin-edit-card" ref={editCardRef}>
          <div className="admin-edit-head">
            <div>
              <p className="eyebrow">Uppdatera</p>
              <h2>Redigera tjänst</h2>
            </div>
            <button className="admin-ghost" onClick={() => setEditingService(null)}>Stäng</button>
          </div>
          <form className="admin-form" onSubmit={handleEditSubmit}>
            <label>
              Titel
              <input name="title" value={editForm.title} onChange={handleEditChange} required />
            </label>
            <label>
              Innehåll
              <textarea name="content" value={editForm.content} onChange={handleEditChange} required />
            </label>
            <div className="form-grid">
              <label>
                Knapp CTA
                <input name="buttonCTA" value={editForm.buttonCTA} onChange={handleEditChange} required />
              </label>
            </div>
            <p className="eyebrow" style={{ marginTop: '0.75rem' }}>Tjänstesida - paneler</p>
            <label>
              Vad ingår - ingress
              <textarea name="includedIntro" value={editForm.includedIntro} onChange={handleEditChange} rows={2} />
            </label>
            <label>
              Vad ingår - en rad per punkt
              <textarea name="includedItemsText" value={editForm.includedItemsText} onChange={handleEditChange} rows={4} />
            </label>
            <label>
              När passar tjänsten - ingress
              <textarea name="fitsIntro" value={editForm.fitsIntro} onChange={handleEditChange} rows={2} />
            </label>
            <label>
              När passar tjänsten - en rad per punkt
              <textarea name="fitsItemsText" value={editForm.fitsItemsText} onChange={handleEditChange} rows={4} />
            </label>
            <label>
              Bild-URL (valfritt)
              <input
                name="image"
                value={editForm.image}
                onChange={handleEditChange}
                placeholder="https://..."
              />
            </label>
            <label>
              Ladda upp ny bild (valfritt)
              <input type="file" accept="image/*" onChange={handleEditFileChange} />
            </label>
            <div className="form-actions">
              <button type="button" className="admin-ghost" onClick={() => setEditingService(null)}>Avbryt</button>
              <button className="primary" type="submit" disabled={saving}>
                {saving ? 'Sparar...' : 'Spara ändringar'}
              </button>
            </div>
          </form>
        </div>
      )}

      {adminMode && adminMessage && <p className="admin-message">{adminMessage}</p>}
    </div>
  );
}

export default Services;